// Shared approval/status logic for Direct Orders.
//
// Approval columns hold: 0/null = not yet actioned, 1-3 = approved (2 and 3 are
// legacy values), 4 = rejected. Orders are approved strictly in order at three
// levels. Who holds each level is set by Admins (Approvers page): the backend sends
// each order's approvalSteps, naming who actually decided a level (as recorded then)
// and who is up next. Nothing about the people is hard-coded here.
import { approverAt } from './approverStore';

export const LEVELS = [
  { key: 'do_firstApproval', level: 1, label: 'First approval' },
  { key: 'do_secondApproval', level: 2, label: 'Second approval' },
  { key: 'do_thirdApproval', level: 3, label: 'Final approval' },
];

const toLevel = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  return parseInt(value, 10) || 0;
};

const isApprovedLevel = (value) => value === 1 || value === 2 || value === 3;
const isRejectedLevel = (value) => value === 4;

/**
 * The three approval steps with a display state:
 *  approved | rejected | current (next to act) | waiting (blocked by an earlier step)
 * Each step has the person's name, position (title) and email.
 */
export const getApprovalSteps = (order) => {
  if (Array.isArray(order?.approvalSteps) && order.approvalSteps.length === LEVELS.length) {
    return order.approvalSteps.map((s, i) => ({ ...LEVELS[i], ...s, key: LEVELS[i].key, level: LEVELS[i].level }));
  }
  // Fallback for orders sent without steps: states from the columns, people from the current chain.
  let blocked = false;
  return LEVELS.map((lvl) => {
    const value = toLevel(order?.[lvl.key]);
    let state;
    if (isRejectedLevel(value)) {
      state = 'rejected';
      blocked = true;
    } else if (isApprovedLevel(value)) {
      state = 'approved';
    } else if (blocked) {
      state = 'waiting';
    } else {
      state = 'current';
      blocked = true;
    }
    const person = approverAt(lvl.level);
    return { ...lvl, state, name: person?.name || lvl.label, title: person?.title || null, email: person?.email || null };
  });
};

/** rejected | approved | in_progress | pending_approval */
export const getOrderStatus = (order) => {
  const levels = LEVELS.map((a) => toLevel(order?.[a.key]));
  if (levels.some(isRejectedLevel)) return 'rejected';
  if (levels.every(isApprovedLevel)) return 'approved';
  if (levels.some(isApprovedLevel)) return 'in_progress';
  return 'pending_approval';
};

/** Queue grouping used by filters: pending covers pending_approval + in_progress. */
export const getOrderGroup = (order) => {
  const status = getOrderStatus(order);
  return status === 'pending_approval' || status === 'in_progress' ? 'pending' : status;
};

/** Short human description, e.g. "Awaiting Etienne Bonello", "Rejected by Francis B. Agius". */
export const getStatusLabel = (order) => {
  const steps = getApprovalSteps(order);
  const rejected = steps.find((s) => s.state === 'rejected');
  if (rejected) return `Rejected by ${rejected.name}`;
  const current = steps.find((s) => s.state === 'current');
  return current ? `Awaiting ${current.name}` : 'Approved';
};

/** Visual tone for a status: approved | rejected | pending */
export const getStatusTone = (order) => {
  const status = getOrderStatus(order);
  if (status === 'approved') return 'approved';
  if (status === 'rejected') return 'rejected';
  return 'pending';
};

/**
 * Whether the user with this email can act on the order now: they hold the level the order is
 * waiting at (the first one not approved, which may have been rejected and resubmitted).
 */
export const canUserApprove = (order, email) => {
  const normalised = (email || '').toLowerCase();
  if (!normalised) return false;
  const steps = getApprovalSteps(order);
  const next = steps.find((s) => s.state !== 'approved');
  if (!next || (next.state !== 'current' && next.state !== 'rejected')) return false;
  return approverAt(next.level)?.email === normalised;
};

/** Approver who is up next, or null when the order is finished or rejected. */
export const getCurrentApprover = (order) => {
  const step = getApprovalSteps(order).find((s) => s.state === 'current');
  return step || null;
};

export const getReference = (order) => order?.doRef || (order?.doId ? `#${order.doId}` : 'N/A');

export const getInitiative = (order) => order?.doOtherInitiative || order?.doInitiativeName || 'N/A';

const euro = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' });

export const formatEuro = (value) => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? euro.format(amount) : '—';
};

export const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
